import { ContentState } from "draft-js";
import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled, { css } from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { allCountrycodes, countries, Country, countryCodes } from "@domain/currentProfile/countryCode";
import { CurrentProfile } from "@domain/currentProfile/currentProfile";
import { ProfileDraft } from "@domain/currentProfile/profileDraft";
import { getBanner } from "@utils/banner";
import { useAsync } from "@utils/hooks";
import { ClosableModal } from "@ui/modals/closableModal";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { PrimaryBold } from "@ui/components/containers/primary";
import { CountryDatalist } from "@ui/components/inputs/countryDatalist";
import { RichTextInput } from "@ui/components/inputs/draft/richTextInput";
import { RoundedTextInput } from "@ui/components/inputs/textInput";
import { stack } from "@ui/components/layout";
import { SizeCounter } from "@ui/components/sizeCounter";
import { EditableProfileImage } from "./editableProfileImage";

const MAX_DESCRIPTION_LENGTH = 80;
const MAX_PRESENTATION_LENGTH = 1200;
interface ProfileEditionModalProps extends React.HTMLAttributes<HTMLDivElement> {
	initialProfile: CurrentProfile;
	close: () => void;
}
export const ProfileEditionModal: React.FC<ProfileEditionModalProps> = ({ initialProfile, close, ...htmlProps }) => {
	const { currentProfileService } = useServices();
	const [profile, setProfile] = useState<Omit<ProfileDraft, "countryCode">>(initialProfile);
	const [country, setCountry] = useState<Country | undefined>(
		initialProfile.countryCode ? countries[initialProfile.countryCode] : undefined
	);
	const descriptionLength = profile.description.length;
	const presentationLength = profile.presentation?.getPlainText().length ?? 0;
	const invalidPrimaryName = profile.primaryName.length === 0;
	const invalidDescription = descriptionLength > MAX_DESCRIPTION_LENGTH;
	const invalidPresentation = presentationLength > MAX_PRESENTATION_LENGTH;
	const history = useHistory();

	const { run, loading } = useAsync(
		() => currentProfileService.saveProfileInfos({ ...profile, countryCode: country && countryCodes[country] }),
		[profile, country]
	);

	return (
		<Container keyboardEventDisabled title={I18n.t("profile.edit.title")} close={close} {...htmlProps}>
			<EditableProfileImage
				type={"banner"}
				image={profile.banner}
				defaultUrl={getBanner(initialProfile.id)}
				label={I18n.t("profile.edit.banner")}
				onUpload={(media) => setProfile((current) => ({ ...current, banner: media }))}
				onDelete={() => setProfile((current) => ({ ...current, banner: undefined }))}
			/>
			<EditableProfileImage
				type={"avatar"}
				image={profile.avatar}
				label={I18n.t("profile.edit.avatar")}
				onUpload={(media) =>
					setProfile((current) => ({ ...current, avatar: media, avatarCrop: undefined, avatarOriginal: media }))
				}
				onDelete={() =>
					setProfile((current) => ({ ...current, avatar: undefined, avatarCrop: undefined, avatarOriginal: undefined }))
				}
			/>
			<div />
			<InfoEdition>
				<PrimaryBold>{I18n.t("profile.edit.firstName")}</PrimaryBold>
				<TextInput
					value={profile.primaryName}
					onChange={(e) => {
						const value = e.currentTarget.value;
						setProfile((current) => ({
							...current,
							primaryName: value,
						}));
					}}
					isInvalid={invalidPrimaryName}
				/>
			</InfoEdition>
			<InfoEdition>
				<PrimaryBold>{I18n.t("profile.edit.lastName")}</PrimaryBold>
				<TextInput
					value={profile.secondaryName ?? ""}
					onChange={(e) => {
						const value = e.currentTarget.value;
						setProfile((current) => ({
							...current,
							secondaryName: value,
						}));
					}}
				/>
			</InfoEdition>
			<InfoEdition>
				<PrimaryBold>{I18n.t("profile.edit.country")}</PrimaryBold>
				<CountryDatalist
					id="countries"
					value={country}
					onChange={(v: string) => setCountry(v as Country)}
					placeholder={I18n.t("onboarding.country.placeholder")}
					options={allCountrycodes}
				/>
			</InfoEdition>
			<InfoEdition>
				<PrimaryBold>{I18n.t("profile.edit.description")}</PrimaryBold>
				<DescriptionInput
					value={profile.description}
					onChange={(e) => {
						const value = e.currentTarget.value;
						setProfile((current) => ({ ...current, description: value }));
					}}
					isInvalid={invalidDescription}
				/>
				<SizeCounter isInvalid={invalidDescription}>{`${descriptionLength}/${MAX_DESCRIPTION_LENGTH}`}</SizeCounter>
			</InfoEdition>
			<InfoEdition>
				<PrimaryBold>{I18n.t("profile.edit.presentation")}</PrimaryBold>
				<PresentationInput
					mentionsEnabled
					value={profile.presentation ?? ContentState.createFromText("")}
					onChange={(value) => setProfile((current) => ({ ...current, presentation: value }))}
					isInvalid={invalidPresentation}
				/>
				<SizeCounter isInvalid={invalidPresentation}>{`${presentationLength}/${MAX_PRESENTATION_LENGTH}`}</SizeCounter>
			</InfoEdition>
			<LoadingButton
				onClick={async () => {
					try {
						const newProfile = await run();
						close();
						history.push(Path.Profile(newProfile));
					} catch {
						createToast(I18n.t("error"), I18n.t("profile.save.error"), "error");
					}
				}}
				loading={loading}
				disabled={
					invalidPrimaryName || invalidDescription || invalidPresentation || !country || !(country in countryCodes)
				}
			>
				{I18n.t("validate")}
			</LoadingButton>
		</Container>
	);
};

const Container = styled(ClosableModal)`
	${gap(30)};

	> button {
		margin: 0 auto;
	}
`;

const InfoEdition = styled.div`
	${stack(10, "flex-start", "stretch")};
`;

const TextInput = styled.input<{ isInvalid?: boolean }>`
	${({ theme }) => theme.containerStyles.roundedBlockStyle};
	padding: 10px 12px 9px;
	border-radius: 10px;
	font-size: 0.935rem;
	outline: none;

	:disabled {
		color: ${({ theme }) => theme.colors.textTertiary};
	}

	:focus {
		border: 1px solid ${({ theme, isInvalid }) => (isInvalid ? theme.colors.red : theme.colors.secondary)};
	}

	${({ isInvalid }) =>
		isInvalid &&
		css`
			border: 1px solid ${({ theme }) => theme.colors.red};
		`}

	@media (max-width: 370px) {
		width: 140px;
	}
`;

const DescriptionInput = styled(RoundedTextInput).withConfig({
	shouldForwardProp: (prop) => prop !== "isInvalid",
})<{ isInvalid?: boolean }>`
	padding: 10px 12px 9px;
	border-radius: 10px;
	font-size: 0.935rem;
	:focus {
		border: 1px solid ${({ theme, isInvalid }) => (isInvalid ? theme.colors.red : theme.colors.secondary)};
	}
	${({ isInvalid }) =>
		isInvalid &&
		css`
			border: 1px solid ${({ theme }) => theme.colors.red};
		`}
`;

const PresentationInput = styled(RichTextInput)<{ isFocused?: boolean; isInvalid?: boolean }>`
	${({ theme }) => theme.containerStyles.roundedBlockStyle};
	border-color: ${({ theme, isFocused, isInvalid }) =>
		isInvalid ? theme.colors.red : isFocused ? theme.colors.secondary : theme.colors.lightergray};
	cursor: text;

	.public-DraftEditor-content,
	.public-DraftEditorPlaceholder-root {
		padding: 15px;
		font-size: 0.9375rem;
	}
`;
